import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { useI18n } from "@/i18n/I18nContext";
import { ManuscriptOrnament } from "@/components/Illustrations";
import ChapterRow, { ChapterCta, ManuscriptNote, ChapterBridge } from "./ChapterRow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Chapter VI — THE LEGACY & ARCHIVE
 *
 * Manuscripts grid + Maestro's Cellar capture. The Work With Us inquiry
 * form lives in its own dedicated Chapter VII so the visitor reaches a
 * clean, focused outreach surface at the end of the journey.
 */
export default function ChapterArchive() {
  const { t, lang } = useI18n();

  const epigraphEN = (
    <>
      <span className="text-tobacco/90">What remains when the smoke is gone?</span>
      <br />
      The notes. The bales. The family record.
    </>
  );
  const epigraphES = (
    <>
      <span className="text-tobacco/90">¿Qué queda cuando se va el humo?</span>
      <br />
      Las notas. Las pacas. El registro de la familia.
    </>
  );

  return (
    <>
      <ChapterRow
        id="archive"
        testid="chapter-archive"
        numeral="VI"
        name="THE ARCHIVE"
        chapterLabel={
          lang === "es"
            ? "Capítulo VI — Legado y Archivo"
            : "Chapter VI — Legacy & Archive"
        }
        epigraph={lang === "es" ? epigraphES : epigraphEN}
        headline={t("archive.title")}
        body={
          <>
            <p className="text-tobacco/85 text-[15.5px] md:text-[15px] leading-[1.7] font-normal">
              {t("archive.intro")}
            </p>
            <p className="text-tobacco/65 italic font-display text-[16px] leading-snug pt-2">
              {lang === "es"
                ? "“Lo que se escribe, se hereda.”"
                : "“What is written, is inherited.”"}
            </p>
          </>
        }
        cta={
          <ChapterCta
            testid="archive-cta"
            onClick={() =>
              document
                .getElementById("manuscripts")
                ?.scrollIntoView({ behavior: "smooth" })
            }
          >
            {lang === "es" ? "Entrar al archivo" : "Enter the archive"} →
          </ChapterCta>
        }
        illustration={
          <ManuscriptOrnament className="w-full h-auto max-h-[260px] md:max-h-[300px]" />
        }
        meta={
          <ManuscriptNote
            label={lang === "es" ? "Permanencia" : "Permanence"}
            items={[
              {
                k: lang === "es" ? "Manuscritos" : "Manuscripts",
                v: lang === "es" ? "Públicos" : "Public",
              },
              {
                k: lang === "es" ? "Bodega" : "Cellar",
                v: lang === "es" ? "Colección privada" : "Private collection",
              },
              {
                k: lang === "es" ? "Inquiry" : "Inquiry",
                v: lang === "es" ? "Bajo cita" : "By appointment",
              },
            ]}
          />
        }
        divider={false}
      />

      <ArchiveEntries lang={lang} t={t} />
      <CellarCapture lang={lang} t={t} />
    </>
  );
}

/* ---------------------------- Archive entries ---------------------------- */
function ArchiveEntries({ lang, t }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/archive`)
      .then((r) => setEntries(r.data))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      id="manuscripts"
      data-testid="archive-manuscripts"
      className="relative bg-pearl text-tobacco py-12 md:py-16 border-t border-tobacco/10"
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 md:gap-10 items-start">
        <div className="col-span-12 md:col-span-3">
          <div className="font-mono text-[10px] tracking-widest2 uppercase text-tobacco/55 mb-4">
            {lang === "es" ? "Capítulo VI · Manuscritos" : "Chapter VI · Manuscripts"}
          </div>
          <h3 className="font-display italic text-tobacco leading-[1.05] text-[26px] md:text-[30px] max-w-[14ch]">
            {lang === "es" ? "Notas del archivo." : "Notes from the archive."}
          </h3>
        </div>

        <div className="col-span-12 md:col-span-7">
          <div className="grid grid-cols-12 gap-px bg-tobacco/15 border border-tobacco/15">
            {loading ? (
              <div className="col-span-12 bg-pearl p-8 font-mono text-[10px] tracking-widest2 uppercase text-tobacco/50">
                Loading…
              </div>
            ) : entries.length === 0 ? (
              <div className="col-span-12 bg-pearl p-8 font-mono text-[10px] tracking-widest2 uppercase text-tobacco/50">
                {t("archive.empty")}
              </div>
            ) : (
              entries.slice(0, 4).map((e, i) => {
                const colSpan =
                  i === 0 ? "col-span-12 md:col-span-6" : "col-span-12 md:col-span-3";
                const folio = String(i + 1).padStart(3, "0");
                return (
                  <motion.article
                    key={e.id}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.9, delay: i * 0.05 }}
                    data-testid={`archive-${e.id}`}
                    className={`${colSpan} bg-pearl group transition-colors duration-700 hover:bg-pearl-warm`}
                  >
                    <Link to={`/archive/${e.id}`} className="block p-5 h-full">
                      <div className="flex items-baseline justify-between font-mono text-[8.5px] tracking-widest2 uppercase text-tobacco/55">
                        <span>{e.category}</span>
                        <span>Folio · {folio}</span>
                      </div>
                      <h4 className="mt-3 font-display italic text-[18px] md:text-[20px] text-tobacco leading-tight">
                        {lang === "es" ? e.title_es : e.title_en}
                      </h4>
                      <p className="mt-2 text-tobacco/65 font-light text-[12.5px] leading-relaxed">
                        {lang === "es" ? e.excerpt_es : e.excerpt_en}
                      </p>
                      <div className="mt-3 inline-flex items-center gap-2 font-mono text-[9px] tracking-widest2 uppercase text-tobacco group-hover:opacity-70 transition-opacity duration-500">
                        {t("archive.read")} <span className="arrow-tick" />
                      </div>
                    </Link>
                  </motion.article>
                );
              })
            )}
          </div>
        </div>

        <aside className="hidden md:block col-span-2 text-right">
          <div className="font-mono text-[11px] tracking-widest2 text-tobacco/85">
            VI · i
          </div>
          <div className="mt-2 font-mono text-[9px] tracking-widest2 uppercase text-gold/85">
            {lang === "es" ? "Manuscritos" : "Manuscripts"}
          </div>
        </aside>
      </div>
    </section>
  );
}

/* ---------------------------- Maestro's Cellar ---------------------------- */
function CellarCapture({ lang, t }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [cellarError, setCellarError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setCellarError(t("cellar.error"));
      return;
    }
    setSubmitting(true);
    setCellarError(null);
    try {
      await axios.post(`${API}/cellar/subscribe`, { email, lang });
      setDone(true);
    } catch (_err) {
      setCellarError(t("cellar.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="cellar"
      data-testid="archive-cellar-inquiry"
      className="relative bg-pearl-warm text-tobacco py-14 md:py-16 border-t border-tobacco/10"
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 md:gap-10 items-center">
        <div className="col-span-12 md:col-span-4" data-testid="maestros-cellar">
          <div className="font-mono text-[10px] tracking-widest2 uppercase text-tobacco/55 mb-3">
            {t("cellar.label")}
          </div>
          <h3 className="font-display italic text-tobacco leading-[1.05] text-[26px] md:text-[32px] max-w-[14ch]">
            {t("cellar.title")}
          </h3>
        </div>

        <div className="col-span-12 md:col-span-8">
          <p className="text-tobacco/80 text-[14.5px] md:text-[15px] leading-[1.7] max-w-[58ch] mb-5">
            {t("cellar.body")}
          </p>
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="done"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="font-display italic text-tobacco/90 text-[18px] md:text-[20px] leading-snug"
                data-testid="cellar-success"
              >
                — {t("cellar.success")}
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={onSubmit}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                data-testid="cellar-form"
                className="max-w-[44ch]"
              >
                <div className="flex items-center gap-2 border-b border-tobacco/40 hover:border-tobacco/80 focus-within:border-tobacco transition-colors duration-700">
                  <input
                    type="email"
                    required
                    placeholder={t("cellar.placeholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 bg-transparent outline-none border-0 text-tobacco py-2.5 font-normal text-[14px] placeholder:text-tobacco/45"
                    data-testid="cellar-input"
                    autoComplete="email"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="font-mono text-[10px] tracking-widest2 uppercase text-tobacco hover:opacity-70 disabled:opacity-50 transition-opacity duration-700 py-2"
                    data-testid="cellar-submit"
                  >
                    {submitting ? t("cellar.sending") : t("cellar.button")}
                  </button>
                </div>
                {cellarError ? (
                  <div
                    className="mt-2 font-mono text-[9px] tracking-widest2 uppercase text-destructive/90"
                    data-testid="cellar-error"
                  >
                    {cellarError}
                  </div>
                ) : null}
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Bridge into Chapter VII */}
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 mt-10 md:mt-12">
        <div className="border-t border-tobacco/15 pt-5 flex flex-wrap items-baseline justify-between gap-3 font-mono text-[10px] tracking-widest2 uppercase text-tobacco/55">
          <ChapterBridge
            left={
              lang === "es"
                ? "El archivo está abierto. Ahora la conversación."
                : "The archive is open. Now the conversation."
            }
            right={
              lang === "es"
                ? "Capítulo VII · Trabaja con nosotros"
                : "Chapter VII · Work With Us"
            }
            testid="bridge-vi-to-vii"
            onClick={() =>
              document.getElementById("work")?.scrollIntoView({ behavior: "smooth" })
            }
          />
        </div>
      </div>
    </section>
  );
}
